import { useMemo } from "react";
import { Link, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";
import UserInfo from "src/components/common/userInfo";
import ServerFooter from "src/components/common/ServerFooter";
import {
    boolAndReducer,
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    pmEmails,
    standbyExceptionEmails,
    standbyWeeks,
    weekDays,
} from "src/components/common/functions";
import { StandbyRow } from "./actions_loaders";

type LoaderData = {
    week: string;
    rows: StandbyRow[];
};

export default function AllStandbyList() {
    const { week, rows } = useRouteLoaderData("AllStandbyList") as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const status = searchParams.get("status") ?? "";
    const query = searchParams.get("q") ?? "";
    const normalizedQuery = query.toLowerCase();
    const pmEmailList = pmEmails as string[];
    const exceptionEmails = standbyExceptionEmails as string[];

    const filteredRows = useMemo(() => rows.filter(row => (
        (!pmEmailList.includes(row.email) || exceptionEmails.includes(row.email)) &&
        (status === "Submitted" ? row.weekData.status === "Submitted" : true) &&
        (status === "Created" ? row.weekData.status !== "Submitted" : true) &&
        normalizedQuery.split(" ").filter(Boolean).map(word => JSON.stringify(row).toLowerCase().includes(word)).reduce(boolAndReducer, true)
    )), [rows, status, normalizedQuery, pmEmailList, exceptionEmails]);

    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 10);
    const currentRows = filteredRows.slice((page - 1) * limit, page * limit);

    const setParam = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) params.set(key, value);
        else params.delete(key);
        if (key !== "page") params.set("page", "1");
        setSearchParams(params, { replace: false });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex flex-wrap align-items-center gap-3">
                <span className = "d-flex align-items-center">
                    <label htmlFor = "standby-status-week" className = "me-2 mb-0">Week</label>
                    <select
                        id = "standby-status-week"
                        className = "form-control inline"
                        value = {week}
                        onChange = {e => navigate({ pathname: `/OutsideStatusStandby/${e.currentTarget.value}`, search: searchParams.toString() })}
                    >
                        {standbyWeeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </span>
                <span className = "d-flex align-items-center">
                    <label htmlFor = "standby-status-filter" className = "me-2 mb-0">Status</label>
                    <select
                        id = "standby-status-filter"
                        className = "form-control inline"
                        value = {status}
                        onChange = {e => setParam("status", e.currentTarget.value)}
                    >
                        <option value = "">All</option>
                        <option>Submitted</option>
                        <option value = "Created">Unsubmitted</option>
                    </select>
                </span>
                <input
                    className = "form-control inline"
                    style = {{ maxWidth: 240 }}
                    type = "text"
                    placeholder = "Search"
                    value = {query}
                    onChange = {e => setParam("q", e.currentTarget.value)}
                />
            </nav>

            {!currentRows.length ? <div className = "cssLoading">Empty</div> : (
                <div className = "table-responsive">
                    <table className = "table text-center tableAllBorder">
                        <thead>
                            <tr className = "stickyHeader">
                                <th>Name</th>
                                {weekDays.map((weekday, index) => (
                                    <th key = {index}>{weekday} ({getDatefromWeekNum(week, index)})</th>
                                ))}
                                <th>Other Info</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map((row) => (
                                <tr key = {row.id || row.email}>
                                    <th>
                                        <div>
                                            <UserInfo name = {row.userName} id = {row.id || row.email} />
                                        </div>
                                        <div>
                                            <span className = "mx-1 p-1">{row.weekData.status || "Created"}</span>
                                        </div>
                                    </th>
                                    {(row.weekData.data ?? []).map((day: any[], dayNum: number) => (
                                        <th key = {dayNum}>
                                            <ul>
                                                {(Array.isArray(day) ? day.slice(1) : []).map((job: any, jobIndex: number) => (
                                                    job?.category ? <li key = {jobIndex}>{job.category}</li> : null
                                                ))}
                                            </ul>
                                        </th>
                                    ))}
                                    <th>
                                        <div>Region: {row.weekData.region || "N/A"}</div>
                                        <div>Notes: {row.weekData.otherNotes || "None"}</div>
                                    </th>
                                    <td>
                                        <Link className = "btn btn-outline-primary" to = {String(row.id)}>Detail</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {filteredRows.length ? <ServerFooter length = {filteredRows.length} /> : null}
        </div>
    );
}
